<template>
  <div
    class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
    role="dialog"
    aria-modal="true"
    aria-label="Save flyer to board"
    @click.self="$emit('close')"
  >
    <div class="grid max-h-[calc(100vh-3rem)] w-full max-w-md grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-950/20">
      <header class="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 class="m-0 text-lg font-semibold text-slate-950">Save to board</h2>
        <button class="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950" type="button" aria-label="Close" @click="$emit('close')">
          <i class="ri-close-line text-xl" aria-hidden="true"></i>
        </button>
      </header>

      <div class="min-h-0 overflow-y-auto overscroll-contain">
        <div v-if="!creatingBoard" class="grid gap-1.5 px-3 py-2.5">
          <button class="nf-primary-button nf-primary-button--compact compact-modal-action" type="button" @click="showCreateBoard">
            + Create Board
          </button>
          <div v-if="boardsLoading" class="px-4 py-4 text-center text-sm font-semibold text-slate-500">
            Loading boards...
          </div>
          <div v-else-if="!boards.length" class="px-4 py-4 text-center text-sm font-semibold text-slate-500">
            No boards found.
          </div>
          <button
            v-for="board in boards"
            :key="board.id"
            class="modal-list-row"
            :class="[
              selectedBoardId === board.id ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-100' : '',
              isBoardSaved(board.id) ? 'border-blue-100 bg-blue-50/60' : ''
            ]"
            type="button"
            :disabled="saveSaving"
            @click="selectBoard(board)"
          >
            <span class="min-w-0">
              <span class="block truncate">{{ board.name }}</span>
              <span v-if="board.description" class="mt-0.5 block truncate text-xs font-medium text-slate-500">{{ board.description }}</span>
            </span>
            <span
              class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-bold"
              :class="isBoardSaved(board.id) ? 'bg-blue-600 text-white' : 'border border-blue-200 text-blue-600'"
            >
              {{ isBoardSaved(board.id) ? 'Saved' : selectedBoardId === board.id ? '✓' : 'Save' }}
            </span>
          </button>
        </div>

        <form v-else class="grid gap-2.5 px-3 py-2.5" @submit.prevent="saveBoard">
          <label class="field-label">
            <span>Board name</span>
            <input
              v-model="newBoardName"
              class="field-input"
              type="text"
              placeholder="Enter board name"
              autocomplete="off"
              autofocus
            />
          </label>
          <div v-if="boardError" class="rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
            {{ boardError }}
          </div>
          <div class="grid gap-2 sm:grid-cols-2">
            <button class="modal-secondary" type="button" :disabled="boardSaving" @click="cancelCreateBoard">Cancel</button>
            <button class="nf-primary-button" type="submit" :disabled="boardSaving">
              {{ boardSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </form>

        <div v-if="saveError" class="mx-3 mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
          {{ saveError }}
        </div>

        <div v-if="!creatingBoard" class="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white px-3 py-3 sm:grid-cols-2">
          <button class="modal-secondary" type="button" :disabled="saveSaving" @click="$emit('close')">Cancel</button>
          <button class="nf-primary-button" type="button" :disabled="saveSaving || !selectedBoardId" @click="saveSelectedBoard">
            {{ saveBoardActionLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'

const props = defineProps({
  flyerId: { type: [String, Number], required: true },
  initialSaved: { type: Boolean, default: false },
  initialBoardId: { type: [String, Number], default: '' }
})

const emit = defineEmits(['close', 'saved-change'])

const session = useSessionStore()
const boards = ref([])
const boardsLoading = ref(false)
const creatingBoard = ref(false)
const boardSaving = ref(false)
const saveSaving = ref(false)
const newBoardName = ref('')
const boardError = ref('')
const saveError = ref('')
const selectedBoardId = ref('')
const savedBoardIds = ref(new Set())

const getCurrentUserId = () => String(session.userId || session.user?.user_id || session.user?.id || '').trim()
const normalizeId = (value) => String(value ?? '').trim()
const isTruthyStatus = (value) => ['1', 'true', 'yes', 'saved'].includes(String(value ?? '').trim().toLowerCase())
const isFalseyStatus = (value) => ['0', 'false', 'no', 'unsaved', 'removed'].includes(String(value ?? '').trim().toLowerCase())
const normalizeSavedStatus = (...values) => {
  const value = values.find((item) => String(item ?? '').trim() !== '')
  if (isTruthyStatus(value)) return true
  if (isFalseyStatus(value)) return false
  return null
}
const isBoardSaved = (boardId) => savedBoardIds.value.has(String(boardId || ''))

const saveBoardActionLabel = computed(() => {
  if (saveSaving.value) return isBoardSaved(selectedBoardId.value) ? 'Unsaving...' : 'Saving...'
  return isBoardSaved(selectedBoardId.value) ? 'Unsave' : 'Save'
})

const extractBoardItems = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.boards,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.boards
  ]

  const source = candidates.find(Array.isArray) || []
  return source
    .map((item, index) => ({
      id: normalizeId(item?.board_id ?? item?.id ?? item?.value ?? index + 1),
      name: String(item?.name ?? item?.board_name ?? item?.title ?? item?.label ?? '').trim(),
      description: String(item?.description ?? item?.desc ?? item?.subtitle ?? '').trim(),
      saved: normalizeSavedStatus(
        item?.saved,
        item?.is_saved,
        item?.save_status,
        item?.saved_status,
        item?.flyer_saved,
        item?.is_flyer_saved
      )
    }))
    .filter((item) => item.name)
}

const savedStateFromResponse = (payload, fallback) => {
  const data = payload?.data && typeof payload.data === 'object' ? payload.data : {}
  return normalizeSavedStatus(
    payload?.saved,
    payload?.is_saved,
    payload?.save_status,
    payload?.saved_status,
    payload?.status,
    payload?.message,
    data?.saved,
    data?.is_saved,
    data?.save_status,
    data?.saved_status,
    data?.status,
    data?.message
  ) ?? fallback
}

const fetchBoards = async () => {
  boardsLoading.value = true
  saveError.value = ''

  try {
    const { data } = await api.listBoardsFlyer({
      user_id: getCurrentUserId(),
      flyer_id: normalizeId(props.flyerId)
    })
    boards.value = extractBoardItems(data)
    const hasScopedSavedState = boards.value.some((board) => board.saved !== null)
    if (hasScopedSavedState) {
      const nextSavedBoardIds = new Set()
      boards.value.forEach((board) => {
        if (board.saved) nextSavedBoardIds.add(board.id)
      })
      savedBoardIds.value = nextSavedBoardIds
      emit('saved-change', { saved: nextSavedBoardIds.size > 0, boardIds: Array.from(nextSavedBoardIds) })
    }
    selectedBoardId.value = selectedBoardId.value || Array.from(savedBoardIds.value)[0] || ''
  } catch (error) {
    boards.value = []
    saveError.value = error?.response?.data?.message || 'Boards could not be loaded.'
  } finally {
    boardsLoading.value = false
  }
}

const showCreateBoard = () => {
  creatingBoard.value = true
  newBoardName.value = ''
  boardError.value = ''
}

const cancelCreateBoard = () => {
  creatingBoard.value = false
  newBoardName.value = ''
  boardError.value = ''
}

const saveBoard = async () => {
  const name = newBoardName.value.trim()
  if (!name) {
    boardError.value = 'Enter board name.'
    return
  }

  boardSaving.value = true
  boardError.value = ''

  try {
    await api.addBoardFlyer({
      user_id: getCurrentUserId(),
      name
    })
    creatingBoard.value = false
    newBoardName.value = ''
    await fetchBoards()
  } catch (error) {
    boardError.value = error?.response?.data?.message || 'Board could not be saved. Please try again.'
  } finally {
    boardSaving.value = false
  }
}

const selectBoard = (board) => {
  if (!board?.id || saveSaving.value) return
  selectedBoardId.value = selectedBoardId.value === board.id ? '' : board.id
  saveError.value = ''
}

const saveSelectedBoard = async () => {
  const board = boards.value.find((item) => item.id === selectedBoardId.value)
  if (!board) {
    saveError.value = 'Select a board first.'
    return
  }

  const userId = getCurrentUserId()
  const flyerId = normalizeId(props.flyerId)
  if (!flyerId || !userId || saveSaving.value) return

  const wasBoardSaved = isBoardSaved(board.id)
  const previousSavedBoardIds = new Set(savedBoardIds.value)
  const optimisticSavedBoardIds = new Set(savedBoardIds.value)
  if (wasBoardSaved) {
    optimisticSavedBoardIds.delete(board.id)
  } else {
    optimisticSavedBoardIds.add(board.id)
  }

  savedBoardIds.value = optimisticSavedBoardIds
  saveSaving.value = true
  saveError.value = ''
  selectedBoardId.value = board.id
  emit('saved-change', { saved: optimisticSavedBoardIds.size > 0, boardIds: Array.from(optimisticSavedBoardIds) })

  try {
    const { data } = await api.saveFlyer({ flyer_id: flyerId, user_id: userId, board_id: board.id })
    const confirmedSaved = savedStateFromResponse(data, !wasBoardSaved)
    const confirmedSavedBoardIds = new Set(previousSavedBoardIds)
    if (confirmedSaved) {
      confirmedSavedBoardIds.add(board.id)
    } else {
      confirmedSavedBoardIds.delete(board.id)
    }
    savedBoardIds.value = confirmedSavedBoardIds
    boards.value = boards.value.map((item) => item.id === board.id ? { ...item, saved: confirmedSaved } : item)
    emit('saved-change', { saved: confirmedSavedBoardIds.size > 0, boardIds: Array.from(confirmedSavedBoardIds) })
    emit('close')
  } catch (error) {
    savedBoardIds.value = previousSavedBoardIds
    emit('saved-change', { saved: previousSavedBoardIds.size > 0, boardIds: Array.from(previousSavedBoardIds) })
    saveError.value = error?.response?.data?.message || `Flyer could not be ${wasBoardSaved ? 'unsaved from' : 'saved to'} this board.`
  } finally {
    saveSaving.value = false
  }
}

onMounted(() => {
  const initialBoardId = normalizeId(props.initialBoardId)
  const initialIds = new Set()
  if (props.initialSaved && initialBoardId) initialIds.add(initialBoardId)
  savedBoardIds.value = initialIds
  selectedBoardId.value = Array.from(initialIds)[0] || ''
  fetchBoards()
})
</script>

<style scoped>
.field-label {
  display: grid;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgb(51 65 85);
}

.field-input {
  min-height: 2.65rem;
  width: 100%;
  border-radius: 0.85rem;
  border: 1px solid rgb(203 213 225);
  padding: 0.65rem 0.85rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: rgb(15 23 42);
  outline: none;
}

.field-input:focus {
  border-color: rgb(59 130 246);
  box-shadow: 0 0 0 4px rgb(219 234 254);
}

.modal-list-row {
  display: flex;
  min-height: 3rem;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-bottom: 1px solid rgb(241 245 249);
  border-radius: 0.85rem;
  padding: 0.65rem 0.75rem;
  text-align: left;
  font-size: 0.92rem;
  font-weight: 600;
  color: rgb(15 23 42);
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.modal-list-row:last-child {
  border-bottom: 0;
}

.modal-list-row:hover {
  background: rgb(248 250 252);
}

.modal-secondary,
.modal-primary {
  min-height: 2.65rem;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-size: 0.9rem;
  font-weight: 700;
}

.modal-secondary {
  border: 1px solid rgb(203 213 225);
  color: rgb(71 85 105);
}

.modal-primary {
  background: rgb(37 99 235);
  color: white;
}

.modal-primary:disabled,
.modal-secondary:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.compact-modal-action {
  min-height: 2.45rem;
  width: 100%;
}
</style>
